<template>
    <b-container fluid class='top-padding'>

        <h3 v-if='!isAuthenticated'>Create Account</h3>
        <h3 v-if='isAuthenticated'>Update Account</h3>
        <p>
            Accounts enable personalized features such as draft clipboards, subscriptions, downloads, and contributing your own drafts.
        </p>
        <b-form :novalidate='true' @submit="onSubmit">
            <b-form-group id="emailInputGroup" :class="{ 'is-invalid': formSubmitAttempted && !isValidEmail()}">
                <b-form-text id="emailInputHelp">Email</b-form-text>
                <b-form-input id="emailInput" type="email" v-model="form.email" required placeholder="Enter email" :class="{ 'is-invalid': formSubmitAttempted && !isValidEmail()}">
                </b-form-input>
                <b-form-invalid-feedback id="emailInputFeedback">
                    Enter a valid email address containing an @ symbol
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="passwordInputGroup" :class="{ 'is-invalid': formSubmitAttempted && !isValidPassword()}">
                <b-form-text id="passwordInputHelp">Password (current or new, must be 6 chars or longer) </b-form-text>
                <b-form-input id="passwordInput" type="password" v-model="form.password" required placeholder="Enter account password" :class="{ 'is-invalid': formSubmitAttempted && !isValidPassword()}">
                </b-form-input>
                <b-form-invalid-feedback id="passwordInputFeedback">
                    Enter your current or desired account password
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="confirmPasswordInputGroup" :class="{ 'is-invalid': formSubmitAttempted && !isValidPasswordConfirmation()}">
                <b-form-text id="passwordInputHelp">Confirm Password</b-form-text>
                <b-form-input id="confirmPasswordInput" type="password" v-model="form.confirmPassword" required placeholder="Confirm account password" :class="{ 'is-invalid': formSubmitAttempted && !isValidPasswordConfirmation()}">
                </b-form-input>
                <b-form-invalid-feedback id="confirmPasswordInputFeedback">
                    Confirm your account password
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="nameInputGroup" :class="{ 'is-invalid': formSubmitAttempted && !isValidName()}">
                <b-form-text id="nameInputHelp">Full name</b-form-text>
                <b-form-input id="nameInput" v-model="form.name" required placeholder="Enter first and last names" :class="{ 'is-invalid': formSubmitAttempted && !isValidName()}">
                </b-form-input>
                <b-form-invalid-feedback id="nameInputFeedback">
                    Enter your full name
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="passwordHintInputGroup">
                <b-form-text id="passwordHintInputHelp">Password Hint (shown if you forget your password)</b-form-text>
                <b-form-input id="passwordHintInput" v-model="form.hint" placeholder="Enter password hint">
                </b-form-input>
            </b-form-group>
            <div class='top-padding'>
                <b-button v-if='!isAuthenticated' type="submit" variant="primary" title='Create Account'>Create Account</b-button>
                <b-button v-if='isAuthenticated' type="submit" variant="primary" title='Update Account'>Update Account</b-button>
                <span v-if='accountCreateUpdateFailed' class='text-danger'>Your account was not created; {{accountCreateUpdateFailedReason}}</span>
            </div>
        </b-form>
        <div v-if='isAuthenticated' class='top-padding'>
            <h2>Subscription</h2>
            <div v-if='subscriptionStatus==="none"' class="right-padding align-top no-vertical-spacing border-box">
                <div class="small-vertical-padding">           
                    Subscribe now to get access to full drafts and many more features!<br/>
                    <b-button variant='primary' to='/subscription'>Continue &raquo;</b-button>
                </div>
            </div>
            <div v-if='subscriptionStatus!=="none"'  class="right-padding align-top no-vertical-spacing border-box">
                <div v-if='subscriptionStatus=="activeRenews"' class="vertical-padding">
                    Your subscription is active and renews on {{subscriptionExpirationDate}}.  Thanks for your support!
                </div>
                <div v-if='subscriptionStatus==="activeSingle"' class="vertical-padding">
                    Your subscription is active until {{subscriptionExpirationDate}}.  Thanks for your support!
                </div>
                <div v-if='subscriptionStatus==="cancelled"' class="vertical-padding">
                    Your subscription is cancelled but you may still use it until {{subscriptionExpirationDate}}.  Please consider subscribing again and thanks for supporting Handweaving.net!
                </div>
                <div v-if='subscriptionStatus==="expired"' class="vertical-padding">
                    Your subscription is expired.  Please consider subscribing again and thanks for supporting Handweaving.net!
                </div>
                <div v-if="!(subscriptionStatus==='expired' || subscriptionStatus==='cancelled')" class="bottom-padding">
                    <b-button variant='primary' to='/subscription'>Change Subscription Details &raquo;</b-button>
                    <span v-if='authUser && subscriptionPaymentDetailsPrompt && subscriptionPaymentDetailsPrompt.trim().length > 0' Current: {{subscriptionPaymentDetailsPrompt}}>
                        {{ subscriptionPaymentDetailsPrompt }}
                    </span>
                </div>
                <div v-if="(subscriptionStatus==='expired' || subscriptionStatus==='cancelled')" class="bottom-padding">
                    <b-button variant='primary' to='/subscription'>Renew Subscription &raquo;</b-button>
                </div>
            </div>
        </div>
        <b-modal auto-focus-button='ok' ref='accountCreatedRef' :title='"Welcome, " + authUser.Name + "!"' ok-only @ok='handleModalOk' :no-close-on-backdrop='true' :no-close-on-esc='true'>
            <div class="d-block text-center">
                Your account was created, thank you for joining Handweaving.net!
            </div>
        </b-modal>
        <b-modal auto-focus-button='ok' ref='accountUpdatedRef' :title='"Welcome, " + authUser.Name + "!"' ok-only @ok='handleModalOk' :no-close-on-backdrop='true' :no-close-on-esc='true'>
            <div class="d-block text-center">
                Your account was updated, thank you for joining Handweaving.net!
            </div>
        </b-modal>        
    </b-container>
</template>

<script>
import dataService from '../mixins/dataService.js'
import utilityService from '../mixins/utilityService.js'
import loginControl from './LoginControl.vue'

export default {
    mixins: [dataService, utilityService],
    data() {
        return {
            // this is the data model for the form
            form: {
                email: '',
                password: '',
                confirmPassword: '',
                name: '',
                hint: ''
            },
            formSubmitAttempted: false,
            loginFailed: false,
            accountCreateUpdateFailed: false,
            accountCreateUpdateFailedReason: '',
            hint: null
        }
    },
    components: {
        loginControl: loginControl
    },
    methods: {
        onSubmit(evt) {
            evt.preventDefault()
            this.formSubmitAttempted = true
            if (!this.entireFormValid()) {
                return false
            }
            if (!this.isAuthenticated) {
                this.postUser(this.form.email.trim(), this.form.password.trim(), this.form.name.trim(), this.form.name.trim(), this.form.hint.trim(), true, 'tl').then(userWithStatus => {
                    this.setPostCreateUpdateStatus(userWithStatus, false)
                })
            }
            else {
                let userId = this.authUser.UserID
                let loginToken = this.authUser.LoginToken
                let preferredTieupOrientation = this.authUserSettings.Orientation
                this.putUser(userId, this.form.email.trim(), this.form.password.trim(), this.form.name.trim(), this.form.name.trim(), this.form.hint.trim(), true, preferredTieupOrientation, loginToken).then(userWithStatus => {
                    this.setPostCreateUpdateStatus(userWithStatus, true)
                })
            }
        },

        setPostCreateUpdateStatus(userWithStatus, isUpdate) {
            this.accountCreateUpdateFailed = false
            this.accountCreateUpdateFailedReason = ''
            if (userWithStatus.status > 0) {
                if (userWithStatus.status === 200) {
                    // either login was successful or user was found but password didn't match
                    if (!this.guidIsEmpty(userWithStatus.data.LoginToken)) {
                        // successful if login token returned
                        this.loginFailed = false
                        // success, persist the user and show message/dialog
                        this.$store.commit('setAuthUser', userWithStatus.data)
                        this.getUserSettings(userWithStatus.data.UserID, userWithStatus.data.LoginToken).then(resp => {
                            this.$store.commit('setAuthUserSettings', resp.data)
                        })
                        window.localStorage.setItem('user', JSON.stringify(userWithStatus.data))
                        if (isUpdate) {
                            this.showAccountUpdatedModal()
                        }
                        else {
                            this.showAccountCreatedModal()
                        }
                    }
                }
                else if (userWithStatus.status >= 400 && userWithStatus.status < 500) {
                    this.accountCreateUpdateFailed = true
                    this.accountCreateUpdateFailedReason = ' another account may already exist with this email address.'
                }
            }
            else {
                this.accountCreateUpdateFailed = true
                this.accountCreateUpdateFailedReason = ' Internet connection problem, please try again once this improves.'
            }
        },

        isValidEmail: function() {
            return this.isValidEmailAddress(this.form.email)
        },

        isValidPassword: function() {
            let password = this.form.password.trim()
            return this.validPasswordFormat(password)
        },

        isValidPasswordConfirmation: function() {
            let confirmPassword = this.form.confirmPassword.trim()
            return this.isValidPassword() && confirmPassword === this.form.password
        },

        isValidName: function() {
            return this.form.name.trim().length > 2
        },

        entireFormValid: function() {
            return this.isValidEmail() && this.isValidPassword() && this.isValidPasswordConfirmation() && this.isValidName()
        },

        handleModalOk: function() {
            this.navigateBackToPreviousPage()
        },

        navigateBackToPreviousPage() {
            this.$router.go(-1)
        },

        showAccountCreatedModal: function() {
            this.$refs.accountCreatedRef.show()
        },

        showAccountUpdatedModal: function() {
            this.$refs.accountUpdatedRef.show()
        }
    },

    computed: {
        subscriptionExpirationDate: function() {
            return this.$store.state.authUser.SubscriptionExpirationDate
        },
        authUser: function() {
            return this.$store.state.authUser
        },
        authUserSettings: function() {
            return this.$store.state.authUserSettings
        },
        subscriptionStatus: function() {
            return this.$store.state.authUser.SubscriptionStatus
        },
        subscriptionPaymentDetailsPrompt: function() {
            return this.$store.state.authUser.subscriptionPaymentDetailsPrompt
        },
        isAuthenticated: function() {
            return this.$store.getters.isAuthenticated
        }
    },

    created() {
        if (this.isAuthenticated) {
            let user = this.$store.state.authUser
            this.form.email = user.Email
            this.form.name = user.Name
            this.form.hint = user.Hint
        }
        this.$store.commit('setPageTitle', 'My Account')
    }

}
</script>


<style>

</style>



// WEBPACK FOOTER //
// src/components/Account.vue